import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';

const EditPrayer = () => {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [prayerText, setPrayerText] = useState('');
  const [missionalScale, setMissionalScale] = useState('');
  const [anyLevel, setAnyLevel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [prayerDescriptor, setPrayerDescriptor] = useState('');
  // Missional scale options with descriptors
  const missionalScaleOptions = [
    { value: -10, label: 'Not Interested' },
    { value: -9, label: 'Curious' },
    { value: -8, label: 'Open to Talk' },
    { value: -7, label: 'Exploring' },
    { value: -6, label: 'Seeking' },
    { value: -5, label: 'Decided to investigate Jesus' },
    { value: -4, label: 'Grasp the truth about Jesus' },
    { value: -3, label: 'Thinks Jesus is real. Don\'t know Him yet' },
    { value: -2, label: 'Understands they have a sin problem' },
    { value: -1, label: 'Acceptance of the Truth' },
    { value: 0, label: 'Brand New Believer' },
    { value: 1, label: 'Basic Faith in place' },
    { value: 2, label: 'Understands their identity in Christ' },
    { value: 3, label: 'Experiencing Change in their life' },
    { value: 4, label: 'Developing stronger faith' },
    { value: 5, label: 'Learning Christian Disciplines' },
    { value: 6, label: 'Assimilated into the Church' },
    { value: 7, label: 'Sharing their faith with others' },
    { value: 8, label: 'Growing in Leadership' },
    { value: 9, label: 'Living into their purpose in Christ' },
  ];

  useEffect(() => {
    if (!currentUser?.isSuperAdmin) return;
    const fetchPrayer = async () => {
      setLoading(true);
      setError('');
      try {
        const docRef = doc(db, 'prayers', id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) throw new Error('Prayer not found.');
        const data = docSnap.data();
        setPrayerText(data.prayerText || '');
        setMissionalScale(data.missionalScale ?? '');
        setAnyLevel(!!data.anyLevel);
        setPrayerDescriptor(data.prayerDescriptor || '');
      } catch (err) {
        setError(err.message || 'Failed to load prayer.');
      } finally {
        setLoading(false);
      }
    };
    fetchPrayer();
  }, [id, currentUser]);

  if (!currentUser?.isSuperAdmin) {
    return <div className="p-8 text-center text-red-500">Access denied. Super admin only.</div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (!prayerText.trim()) throw new Error('Prayer text is required.');
      const docRef = doc(db, 'prayers', id);
      await updateDoc(docRef, {
        prayerText,
        missionalScale: missionalScale === '' ? null : Number(missionalScale),
        anyLevel,
        prayerDescriptor,
      });
      navigate('/prayers-management');
    } catch (err) {
      setError(err.message || 'Failed to update prayer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Edit Prayer</h1>
      {loading ? (
        <div className="text-center py-8">Loading prayer...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 font-medium">Descriptor Phrase</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={prayerDescriptor}
              onChange={e => setPrayerDescriptor(e.target.value)}
              placeholder="e.g. Encouragement, Hope, Wisdom"
              required
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Prayer Text</label>
            <textarea
              className="w-full border rounded px-3 py-2"
              rows={4}
              value={prayerText}
              onChange={e => setPrayerText(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Missional Scale</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={missionalScale}
              onChange={e => setMissionalScale(e.target.value)}
              required
            >
              <option value="">Select Missional Scale...</option>
              {missionalScaleOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.value} ({opt.label})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="anyLevel"
              checked={anyLevel}
              onChange={e => setAnyLevel(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="anyLevel" className="font-medium">Any Level</label>
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="flex gap-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              onClick={() => navigate('/prayers-management')}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditPrayer; 